import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Search, ShoppingBag, ShoppingCart, User } from "lucide-react";
import { useI18n } from "@/i18n";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const shopApi = axios.create({ baseURL: `${BACKEND}/api/public/shop` });
const img = (url) => (url ? `${BACKEND}${url}` : null);

function AvailabilityBadge({ availability }) {
  const { t } = useI18n();
  const map = {
    in_stock: ["bg-emerald-100 text-emerald-700", t("shop.storefront.in_stock")],
    backorder: ["bg-amber-100 text-amber-700", t("shop.storefront.backorder")],
    out_of_stock: ["bg-slate-200 text-slate-600", t("shop.storefront.out_of_stock")],
  };
  const [cls, label] = map[availability?.state] || map.out_of_stock;
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} data-testid="sf-availability">{label}</span>;
}

function ProductCard({ p, slug, lang, accent }) {
  const { t } = useI18n();
  return (
    <Link to={`/shop/${slug}/p/${p.id}?lang=${lang}`} data-testid={`sf-product-${p.id}`}
      className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition-all hover:-translate-y-0.5 hover:shadow-lg">
      <div className="flex aspect-square items-center justify-center overflow-hidden bg-slate-50">
        {p.image_url
          ? <img src={img(p.image_url)} alt={p.name} className="h-full w-full object-cover transition-transform group-hover:scale-105" loading="lazy" />
          : <ShoppingBag className="h-14 w-14 text-slate-300" />}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        {p.manufacturer && <span className="text-xs font-medium uppercase tracking-wide text-slate-400">{p.manufacturer}</span>}
        <h3 className="line-clamp-2 text-sm font-semibold text-slate-800">{p.name}</h3>
        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
          <div className="min-w-0">
            <div className="whitespace-nowrap text-lg font-bold" style={{ color: accent }} data-testid="sf-price">€&nbsp;{p.price_gross}</div>
            <div className="text-[11px] text-slate-400">{t("shop.storefront.incl_vat")}</div>
          </div>
          <AvailabilityBadge availability={p.availability} />
        </div>
      </div>
    </Link>
  );
}

export default function Storefront() {
  const { slug } = useParams();
  const { t, locale, setLocale } = useI18n();
  const [shop, setShop] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [state, setState] = useState("loading"); // loading | ready | error | maintenance
  const langInit = useRef(false);
  const accent = shop?.accent_color || "#2563eb";
  const primary = shop?.primary_color || "#0f172a";

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await shopApi.get(`/${slug}`);
        if (!alive) return;
        setShop(data.shop); setCats(data.categories || []);
        if (!langInit.current) { setLocale(data.shop.default_language || "de"); langInit.current = true; }
        setState(data.maintenance ? "maintenance" : "ready");
      } catch { if (alive) setState("error"); }
    })();
    return () => { alive = false; };
  }, [slug, setLocale]);

  const loadProducts = useCallback(async () => {
    try {
      const { data } = await shopApi.get(`/${slug}/products`, { params: { q, category: cat, lang: locale } });
      setItems(data.items || []);
    } catch { setItems([]); }
  }, [slug, q, cat, locale]);

  useEffect(() => { if (state === "ready") { const id = setTimeout(loadProducts, 200); return () => clearTimeout(id); } }, [state, loadProducts]);

  const title = useMemo(() => shop?.shop_name || slug, [shop, slug]);
  const tagline = shop ? (locale === "en" ? shop.tagline_en : shop.tagline_de) : "";

  if (state === "loading") return <div className="flex min-h-screen items-center justify-center bg-slate-50"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  if (state === "error") return <div className="flex min-h-screen items-center justify-center bg-slate-50 p-6 text-center text-slate-500" data-testid="sf-error">{t("shop.storefront.error")}</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="storefront">
      <header className="border-b border-slate-200 bg-white" style={{ borderTopColor: accent, borderTopWidth: 4 }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-4">
          <Link to={`/shop/${slug}`} className="flex items-center gap-3">
            {shop?.logo_url ? <img src={shop.logo_url} alt={title} className="h-9 w-auto" /> :
              <span className="flex h-9 w-9 items-center justify-center rounded-lg text-sm font-bold text-white" style={{ background: primary }}>{title.slice(0, 1)}</span>}
            <div>
              <div className="font-bold text-slate-900" data-testid="sf-shop-name">{title}</div>
              {tagline && <div className="text-xs text-slate-500">{tagline}</div>}
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to={`/shop/${slug}/account`} data-testid="sf-account-link" className="rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200" aria-label="account">
              <User className="h-4 w-4" />
            </Link>
            <Link to={`/shop/${slug}/cart`} data-testid="sf-cart-link" className="relative rounded-full bg-slate-100 p-2 text-slate-600 hover:bg-slate-200">
              <ShoppingCart className="h-4 w-4" />
            </Link>
            <div className="flex items-center gap-1 rounded-full bg-slate-100 p-1 text-xs font-medium">
              {["de", "en"].map((l) => (
                <button key={l} onClick={() => setLocale(l)} data-testid={`sf-lang-${l}`}
                  className={`rounded-full px-3 py-1 ${locale === l ? "bg-white shadow text-slate-900" : "text-slate-500"}`}>{l.toUpperCase()}</button>
              ))}
            </div>
          </div>
        </div>
      </header>

      {state === "maintenance" ? (
        <div className="mx-auto max-w-6xl px-4 py-24 text-center text-slate-500" data-testid="sf-maintenance">{t("shop.storefront.maintenance")}</div>
      ) : (
        <main className="mx-auto max-w-6xl px-4 py-8">
          <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("shop.storefront.search")} data-testid="sf-search"
                className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none focus:border-slate-400" />
            </div>
          </div>
          {cats.length > 0 && (
            <div className="mb-6 flex flex-wrap gap-2" data-testid="sf-categories">
              <button onClick={() => setCat("")} className={`rounded-full px-3 py-1.5 text-sm ${!cat ? "text-white" : "bg-white text-slate-600 border border-slate-200"}`} style={!cat ? { background: primary } : {}}>{t("shop.storefront.all_categories")}</button>
              {cats.map((c) => (
                <button key={c.id} onClick={() => setCat(c.id)} data-testid={`sf-cat-${c.id}`}
                  className={`rounded-full px-3 py-1.5 text-sm ${cat === c.id ? "text-white" : "bg-white text-slate-600 border border-slate-200"}`}
                  style={cat === c.id ? { background: primary } : {}}>{locale === "en" ? (c.name_en || c.name_de) : c.name_de}</button>
              ))}
            </div>
          )}
          {items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-24 text-center text-slate-400" data-testid="sf-empty">{t("shop.storefront.no_products")}</div>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="sf-grid">
              {items.map((p) => <ProductCard key={p.id} p={p} slug={slug} lang={locale} accent={accent} />)}
            </div>
          )}
        </main>
      )}

      <footer className="mt-12 border-t border-slate-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-xs text-slate-500 sm:flex-row sm:items-center sm:justify-between">
          <span>© {new Date().getFullYear()} {shop?.legal?.legal_name || title}</span>
          <span className="text-slate-400">Mayr-Technologies ERP</span>
        </div>
      </footer>
    </div>
  );
}
